import { ChangeEvent, Ref, useImperativeHandle, useRef } from 'react'

import { MinusIcon } from '../../Icons/MinusIcon'
import { PlusIcon } from '../../Icons/PlusIcon'
import { BaseInput, BaseInputProps } from '../BaseInput'
import {
  NumberInputStepButton,
  NumberInputStepperGroup,
  NumberInputStepperSeparator,
} from './NumberInput.parts'

export interface NumberInputProps extends BaseInputProps {
  /**
   * - `0`: integers only
   * - `>0`: max decimal places
   * - `undefined`: no limit
   */
  decimalPlaces?: number
  hideStepper?: boolean
  isDisabled?: boolean
  stepperSize?: 'sm' | 'md'
  ref?: Ref<HTMLInputElement>
}

export const NumberInput = ({
  stepperSize,
  decimalPlaces,
  hideStepper,
  isDisabled,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  ref,
  ...props
}: NumberInputProps) => {
  const internalRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => internalRef.current!)

  const formatValue = (value: number): string => {
    if (decimalPlaces === undefined) {
      return value.toString()
    }
    if (decimalPlaces === 0) {
      return Math.round(value).toString()
    }
    // Round to max decimal places, then remove trailing zeros
    const multiplier = Math.pow(10, decimalPlaces)
    const rounded = Math.round(value * multiplier) / multiplier
    // Convert to string and remove trailing zeros
    return rounded.toString()
  }

  const changeValue = (value: string) => {
    if (!internalRef.current) return
    internalRef.current.value = `${value}`
    internalRef.current.dispatchEvent(new Event('change', { bubbles: true }))
    // React (and react-hook-form) only respond to React's synthetic events.
    // Invoke the registered onChange so the form state updates when using the stepper.
    const syntheticEvent = {
      target: internalRef.current,
      currentTarget: internalRef.current,
    } as ChangeEvent<HTMLInputElement>
    onChangeProp?.(syntheticEvent)
  }

  const clamp = (n: number): number => {
    const min = props.min != null ? Number(props.min) : -Infinity
    const max = props.max != null ? Number(props.max) : Infinity
    return Math.min(max, Math.max(min, n))
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || value === '-') {
      onChangeProp?.(e)
      return
    }
    const num = Number(value)
    if (!isNaN(num) && internalRef.current) {
      const clamped = clamp(num)
      if (clamped !== num) {
        internalRef.current.value = String(clamped)
        const syntheticEvent = {
          target: internalRef.current,
          currentTarget: internalRef.current,
        } as ChangeEvent<HTMLInputElement>
        onChangeProp?.(syntheticEvent)
        return
      }
    }
    onChangeProp?.(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || value === '-') {
      onBlurProp?.(e)
      return
    }

    let numValue = Number(value)
    if (isNaN(numValue)) {
      onBlurProp?.(e)
      return
    }

    numValue = clamp(numValue)
    const formatted =
      decimalPlaces !== undefined ? formatValue(numValue) : String(numValue)
    if (value !== formatted && internalRef.current) {
      internalRef.current.value = formatted
      internalRef.current.dispatchEvent(new Event('change', { bubbles: true }))
      const syntheticEvent = {
        target: internalRef.current,
        currentTarget: internalRef.current,
      } as ChangeEvent<HTMLInputElement>
      onChangeProp?.(syntheticEvent)
    }

    onBlurProp?.(e)
  }

  return (
    <BaseInput
      ref={internalRef}
      type="number"
      pr="0"
      step={decimalPlaces === 0 ? 1 : (props.step ?? 'any')}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={isDisabled}
      rightInputElement={
        hideStepper ? undefined : (
          <NumberInputStepperGroup my="-8px" size={stepperSize}>
            <NumberInputStepButton
              aria-label="decrement"
              onClick={() => {
                const stepSize = Number(props.step ?? 1)
                const currentValue = internalRef.current?.value
                const numValue = currentValue ? Number(currentValue) : 0
                const newValue = clamp(numValue - stepSize)

                changeValue(formatValue(newValue))
              }}
              icon={<MinusIcon boxSize="9px" />}
              // size={stepperSize}
            />
            <NumberInputStepperSeparator />
            <NumberInputStepButton
              icon={<PlusIcon boxSize="9px" />}
              aria-label="increment"
              onClick={() => {
                const stepSize = Number(props.step ?? 1)
                const currentValue = internalRef.current?.value
                const numValue = currentValue ? Number(currentValue) : 0
                const newValue = clamp(numValue + stepSize)

                changeValue(formatValue(newValue))
              }}
              mr="-8px"
              // size={stepperSize}
            />
          </NumberInputStepperGroup>
        )
      }
      {...props}
    />
  )
}
